import Spinner from "@/components/Spinner";
import { PlannerContext } from "@/contexts/PlannerContext";
import { useStores } from "@/hooks/useStores";
import fetchCreatePlanner from "@/lib/planner/fetchCreatePlanner";
import { CalendarIcon, CheckIcon } from "@heroicons/react/24/outline";
import usePaywall from "@hooks/usePaywall";
import clsx from "clsx";
import { observer } from "mobx-react";
import Link from "next/link";
import { useContext, useState } from "react";

const AddMealPlanRecipesToPlanner = observer(
  ({ mealPlanId, plannerTitle = "", recipeIds = [], theme = "light" }) => {
    const { authStore } = useStores();
    const { currentUser } = authStore;
    const { setPaywall } = usePaywall();

    const [newPlanner, setNewPlanner] = useState(null);

    const { addPlanner } = useContext(PlannerContext);

    const [isCreating, setIsCreating] = useState(false);

    const openPaywall = () => {
      setPaywall({
        isOpen: true,
        isClosable: true,
        variant: "createPersonalisedMealPlans",
      });
    };

    const handleClick = async () => {
      setIsCreating(true);

      const title = plannerTitle;

      const user = {
        id: currentUser.id,
        uid: currentUser.uid,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        email: currentUser.email,
      };

      const planner = await fetchCreatePlanner(title, user, false, {
        mealPlanId,
        recipeIds,
      });

      // Add the planner to the context
      addPlanner(planner);

      // And add it to local state
      setNewPlanner(planner);

      setIsCreating(false);
    };

    // Determine the icon: if the planner is already in state, show a checkmark, otherwise show a plus, and if it's being created, show a spinner
    const icon = !!newPlanner ? (
      <CheckIcon className={clsx("h-5 w-5")} />
    ) : isCreating ? (
      <Spinner size={"md"} />
    ) : (
      <CalendarIcon className={clsx("h-5 w-5")} />
    );

    return (
      (<div className={clsx("space-y-6")}>
        <div className={clsx("flex items-center space-x-4")}>
          <button
            className={clsx(
              "w-component h-component border-1 relative flex shrink-0 items-center justify-center rounded-full bg-zinc-950 text-zinc-50 transition ease-out hover:bg-zinc-700 hover:text-zinc-50"
            )}
            onClick={currentUser ? handleClick : openPaywall}
            disabled={isCreating || !!newPlanner}
          >
            {icon}
          </button>
          {!newPlanner && (
            <div
              className={clsx(
                "pr-12 font-body text-sm text-current opacity-80"
              )}
            >
              Add this meal plan to your Planner.
            </div>
          )}
          {!!newPlanner && (
            <div
              className={clsx(
                "pr-12 font-body text-sm font-normal text-current"
              )}
            >
              Your meal plan is ready!{" "}
              <Link
                href={`/planner/${newPlanner.uid}`}
                className={clsx(
                  "underline decoration-current decoration-1 underline-offset-2 transition ease-out hover:opacity-50"
                )}>
                
                  Take a look and make it your own.
                
              </Link>
            </div>
          )}
        </div>
      </div>)
    );
  }
);

export default AddMealPlanRecipesToPlanner;
